import React from "react";
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useUserCategoriesWithRules } from "./hooks/useUserCategoriesWithRules";

const RulesTable = ({ rules }) => {
  if (rules.length === 0) {
    return (
      <Typography color="textSecondary" variant="body2">
        No rules target this category.
      </Typography>
    );
  }

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Priority</TableCell>
          <TableCell>Action</TableCell>
          <TableCell>Condition Type</TableCell>
          <TableCell>Match Value</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rules.map((rule, idx) => (
          <TableRow key={`${rule.ruleId}-${rule.matchId}-${idx}`}>
            <TableCell>{rule.priority}</TableCell>
            <TableCell>{rule.actionType}</TableCell>
            <TableCell>{rule.conditionType}</TableCell>
            <TableCell>{rule.matchValue}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export const UserCategoriesSection = ({ userId }) => {
  const { data, isLoading } = useUserCategoriesWithRules(userId);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const userCategories = data?.userCategories ?? [];
  const unassignedRules = data?.unassignedRules ?? [];

  return (
    <Box>
      {userCategories.length === 0 && (
        <Typography color="textSecondary">
          No user categories found.
        </Typography>
      )}

      {userCategories.map((category) => (
        <Accordion key={category.id}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography>{category.name}</Typography>
              {category.archived && (
                <Chip label="Archived" size="small" />
              )}
              {category.shouldExcludeFromBudget && (
                <Chip
                  label="Excluded from Budget"
                  size="small"
                  color="warning"
                />
              )}
              <Typography variant="caption" color="textSecondary">
                {category.rules.length} rule
                {category.rules.length === 1 ? "" : "s"}
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <RulesTable rules={category.rules} />
          </AccordionDetails>
        </Accordion>
      ))}

      {unassignedRules.length > 0 && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography>Other Rules (Flag / Clear Category)</Typography>
              <Typography variant="caption" color="textSecondary">
                {unassignedRules.length} rule
                {unassignedRules.length === 1 ? "" : "s"}
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <RulesTable rules={unassignedRules} />
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
};
